import { useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { errorMessage } from '../../lib/errors';
import { MIN_PASSWORD_LENGTH, describePasswordRule } from '../../lib/password';
import { Loader2, CheckCircle2, AlertTriangle, Circle, Eye, EyeOff } from 'lucide-react';
import { AuthShell } from './AuthShell';

// Same frame and identity as the sign-in screen (AuthShell + auth.css) — this
// screen is part of the same auth moment and must match it.

type Phase = 'checking' | 'ready' | 'no-session' | 'saved';

/**
 * Where a Supabase recovery link lands.
 *
 * The link carries a one-time token that supabase-js exchanges for a session
 * automatically (detectSessionInUrl is on by default), so by the time this
 * mounts the user is normally signed in and `updateUser` will work. That is
 * also why this route is intercepted BEFORE the session gate in App.tsx: the
 * recovery session is a real session, and without the intercept the user would
 * be dropped straight into the dashboard having never set a password.
 *
 * The exchange is not instant and can fail (expired link, already used, opened
 * in a different browser), so this waits for the auth event rather than reading
 * the session once, and has an honest dead end for when no session arrives.
 */
export function ResetPasswordScreen() {
    const navigate = useNavigate();
    const [phase, setPhase] = useState<Phase>('checking');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [show, setShow] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const ids = useId();
    const newId = `${ids}-new`;
    const confirmId = `${ids}-confirm`;
    const ruleId = `${ids}-rule`;

    useEffect(() => {
        let settled = false;

        // PASSWORD_RECOVERY fires once supabase-js has consumed the token from
        // the URL. Listening is more reliable than a single getSession() read,
        // which races the exchange.
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            if (event === 'PASSWORD_RECOVERY' || session) {
                settled = true;
                setPhase('ready');
            }
        });

        // ------------------------------------------------------------------
        // Two link shapes, both supported on purpose.
        //
        // (a) ?token_hash=…&type=recovery  — the email template points straight
        //     at app.forgedash.uk and we redeem the token here. This is the
        //     shape we want: Resend flags a mismatch between the sending domain
        //     (forgedash.uk) and the link domain, and a reset email whose only
        //     link goes to a *.supabase.co host is a reliable way into the spam
        //     folder. Keeping the link on our own domain removes that signal.
        //
        // (b) No token_hash — the default Supabase link, which routes through
        //     /auth/v1/verify and arrives here with the session already
        //     established (or a #access_token fragment for supabase-js to eat).
        //     Still handled because links already sent are still in inboxes,
        //     and because reverting the email template must not break resets.
        // ------------------------------------------------------------------
        const params = new URLSearchParams(window.location.search);
        const tokenHash = params.get('token_hash');

        if (tokenHash) {
            supabase.auth
                .verifyOtp({ token_hash: tokenHash, type: 'recovery' })
                .then(({ error: verifyError }) => {
                    settled = true;
                    if (verifyError) {
                        setPhase('no-session');
                        return;
                    }
                    // Drop the token from the address bar so a browser-history
                    // step, or a shoulder-surfed URL, cannot replay it.
                    window.history.replaceState({}, '', '/reset-password');
                    setPhase('ready');
                });
        } else {
            supabase.auth.getSession().then(({ data }) => {
                if (data.session) {
                    settled = true;
                    setPhase('ready');
                }
            });
        }

        // Give the exchange a moment before declaring the link dead. Without
        // this the screen flashes "link expired" on every successful reset.
        const timer = setTimeout(() => {
            if (!settled) setPhase('no-session');
        }, 3000);

        return () => {
            subscription.unsubscribe();
            clearTimeout(timer);
        };
    }, []);

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (password.length < MIN_PASSWORD_LENGTH) {
            setError(describePasswordRule());
            return;
        }
        if (password !== confirm) {
            setError('Those two passwords do not match.');
            return;
        }

        setSaving(true);
        try {
            const { error: err } = await supabase.auth.updateUser({ password });
            if (err) throw err;
            setPhase('saved');
            // Straight into the app — the recovery session is already a real
            // signed-in session, so there is nothing further to do.
            setTimeout(() => navigate('/', { replace: true }), 1800);
        } catch (err) {
            setError(errorMessage(err) || 'Could not set your new password.');
        } finally {
            setSaving(false);
        }
    };

    const shell = (children: React.ReactNode) => (
        <AuthShell>
            <div className="fda-view">{children}</div>
        </AuthShell>
    );

    if (phase === 'checking') {
        return shell(
            <div className="fda-done" role="status">
                <div className="fda-done__icon" aria-hidden="true"><Loader2 className="fda-spin" /></div>
                <p className="fda-sub">Checking your reset link…</p>
            </div>
        );
    }

    if (phase === 'no-session') {
        return shell(
            <div className="fda-done">
                <div className="fda-done__icon fda-done__icon--warn" aria-hidden="true"><AlertTriangle /></div>
                <h1 className="fda-title">This link has expired</h1>
                <p className="fda-sub">
                    Password reset links can only be used once, and they expire. Request a fresh one
                    from the sign-in screen.
                </p>
                <button type="button" onClick={() => navigate('/?action=signin', { replace: true })}
                    className="fda-btn fda-btn--primary">
                    Back to sign in
                </button>
            </div>
        );
    }

    if (phase === 'saved') {
        return shell(
            <div className="fda-done" role="status">
                <div className="fda-done__icon" aria-hidden="true"><CheckCircle2 /></div>
                <h1 className="fda-title">Password updated</h1>
                <p className="fda-sub">Taking you to your workshop…</p>
            </div>
        );
    }

    return shell(
        <>
            <div className="fda-head">
                <p className="fda-kicker">Password reset</p>
                <h1 className="fda-title">Set a new password</h1>
                <p className="fda-sub">Choose something you have not used elsewhere.</p>
            </div>

            <form onSubmit={handleSave} className="fda-form">
                {error && <p className="fda-msg fda-msg--error" role="alert">{error}</p>}

                <div className="fda-field">
                    <label className="fda-label" htmlFor={newId}>New password</label>
                    <div className="fda-input-wrap">
                        <input id={newId} type={show ? 'text' : 'password'} required autoFocus
                            autoComplete="new-password" minLength={MIN_PASSWORD_LENGTH}
                            className="fda-input fda-input--pw" aria-describedby={ruleId}
                            value={password} onChange={(e) => setPassword(e.target.value)} />
                        <button type="button" className="fda-reveal" onClick={() => setShow(!show)}
                            aria-label={show ? 'Hide passwords' : 'Show passwords'} aria-pressed={show}
                            aria-controls={`${newId} ${confirmId}`}>
                            {show ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                        </button>
                    </div>
                </div>

                <div className="fda-field">
                    <label className="fda-label" htmlFor={confirmId}>Confirm new password</label>
                    <input id={confirmId} type={show ? 'text' : 'password'} required
                        autoComplete="new-password"
                        className="fda-input"
                        value={confirm} onChange={(e) => setConfirm(e.target.value)} />
                </div>

                <p id={ruleId} className="fda-rule" data-met={password.length >= MIN_PASSWORD_LENGTH}>
                    {password.length >= MIN_PASSWORD_LENGTH ? <CheckCircle2 aria-hidden="true" /> : <Circle aria-hidden="true" />}
                    <span>{describePasswordRule()}</span>
                </p>

                <button type="submit" disabled={saving || !password || !confirm} className="fda-btn fda-btn--primary">
                    {saving ? <><Loader2 className="fda-spin" aria-hidden="true" /> Saving…</> : 'Save new password'}
                </button>
            </form>
        </>
    );
}
